import { wccModules } from 'enums/wccModules';
import { BlobWrapper } from 'interfaces/blobWrapper';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, SubscribableOrNullableValue } from 'knockout';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import './videoRecorder.html';
import './videoRecorder.scss';
import device from 'managers/device';
import { DOMHelpers } from 'helpers/dom';

const { availableViewportSizeToOverlay } = settings;

const videoSettings: MediaTrackConstraints = {
    width: { min: 1280, ideal: 1280 },
    height: { min: 720, ideal: 720 }
}

export interface WCCVideoRecorderConfig {
    maxRecordingTime: SubscribableOrNullableValue<number>
    fitViewport: SubscribableOrNullableValue<boolean>
    onRecorded?: Action<[BlobWrapper]>
}

/**
 * Records video using media streams
 * */
@injectable()
export default class WCCVideoRecorder {
    private startedRecordingOn: Observable<Date | undefined> = ko.observable()
    private isRecordingAllowed: PureComputed<boolean>

    previewNode: Observable<HTMLVideoElement | undefined> = ko.observable()

    availableTimeLabel: PureComputed<string>

    isSupported = ko.observable(true)
    isDataReady = ko.observable(false)    
    isRecordingRequested = ko.observable(false)

    constructor(
        @inject(wccModules.componentConfig) config: WCCVideoRecorderConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const maxRecordingTime = ko.flattenComputed(config.maxRecordingTime).default(300);
        const shouldFitViewport = ko.flattenComputed(config.fitViewport, false);

        const onRecorded = config.onRecorded ?? (blob => console.log(blob));

        const availableTime = ko.pureComputed(() => {
            const totalTime = maxRecordingTime();
            const startedOn = this.startedRecordingOn();

            let result = totalTime;
            
            if (startedOn != undefined) {
                const timeUsed = (settings.now().getTime() - startedOn.getTime()) / 1000;

                if (timeUsed > 0)
                    result -= timeUsed;

                if (result < 0)
                    result = 0;
            }

            return result;
        });

        this.availableTimeLabel = availableTime.mapSingle(seconds => moment().startOf('day').add(seconds, 'seconds').format('mm:ss'));

        this.isRecordingAllowed = ko.pureComputed(() => this.isRecordingRequested() && availableTime() > 0);        

        const stream: Observable<MediaStream | undefined> = ko.observable();

        /**
         * get stream and saved it into ko container
         **/
        effects.register(async () => {
            try {
                const requestedStream = await this.requestStream();
                stream(requestedStream);

                return () => {
                    requestedStream.getTracks().forEach(track => track.stop()); //release requested media stream
                    stream(undefined);
                }
            } catch {
                this.isSupported(false);
            }
        });

        /**
         * once we have preview node and stream wire them together
         * so we can see video from camera
         **/ 
        effects.register((node, stream) => {
            if (node != undefined && stream != undefined) {
                const effects = [
                    DOMHelpers.onNodeEvent(node, 'canplay', () => this.isDataReady(true)),
                    () => this.isDataReady(false)
                ];

                try {
                    node.src = URL.createObjectURL(stream);
                } catch {
                    node.srcObject = stream;
                }        

                return effects;
            }
        }, [this.previewNode, stream]);

        /**
         * record video once stream is awailable and recording is allowed
         **/ 
        effects.register(stream => {
            if (stream != undefined) {
                return withEffect(isRecordingAllowed => {
                    if (isRecordingAllowed) {
                        const recorder = this.setupRecorder(stream, onRecorded);

                        recorder.start();
                        this.startedRecordingOn(new Date());

                        return () => {
                            recorder.stop();
                            this.startedRecordingOn(undefined);
                            this.isRecordingRequested(false);
                        }
                    }
                }, [this.isRecordingAllowed]);                
            }
        }, [stream]);

        const maxWidth = ko.pureComputed(() => shouldFitViewport() ? device.width() * availableViewportSizeToOverlay : undefined);
        const maxHeight = ko.pureComputed(() => shouldFitViewport() ? device.height() * availableViewportSizeToOverlay : undefined);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-video-recorder': true,
                'wcc-video-recorder--fit-viewport': true
            },

            'style': {
                'max-width': maxWidth.toPx(),
                'max-height': maxHeight.toPx()
            }
        });
    }

    /**
     * toggles flag which allows recording
     * */
    toggleRecording = () => {
        this.isRecordingRequested.toggle();
    }

    /**
     * create new recorder and prepare it for collecting data
     * actual recording will start on .start() call on recorder
     * once .stop is called on recorder new blob will be generated and push into onRecorded callback
     * @param stream
     * @param onRecorded
     */
    private setupRecorder(stream: MediaStream, onRecorded: Action<[BlobWrapper]>) {
        const recorder = new MediaRecorder(stream);
        const chunks: Array<Blob> = [];

        recorder.addEventListener('dataavailable', e => {
            if (e.data.size > 0)
                chunks.push(e.data);

            if (recorder.state === 'inactive' && chunks.length > 0) {
                const blob = new Blob(chunks, { type: 'video/mp4' });
                const name = `Video recording - ${new Date().toLocaleDateString()}.mp4`;

                onRecorded({ blob, name });
            }
        });

        return recorder;
    }

    /**
     * Request video with audio (e.g. webcam) stream from browser 
     * with fallback to video only (e.g. some screen recording codecs).
     * */
    private async requestStream() {
        try {
            return await navigator.mediaDevices.getUserMedia({ video: videoSettings, audio: true });
        } catch {
            return await navigator.mediaDevices.getUserMedia({ video: videoSettings });            
        }
    }
}